import {attachSearchBox, type SearchResult} from '../../../js/modules/search.ts';

const {appSubUrl} = window.config;

export function initAgentActionMatrix(): void {
  const rows = document.querySelectorAll<HTMLElement>('.js-agent-action-row');
  if (!rows.length) return;

  for (const row of rows) {
    const enabledInput = row.querySelector<HTMLInputElement>('.js-agent-capability');
    if (!enabledInput) continue;

    const settingsInputs = row.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('.js-agent-action-settings input, .js-agent-action-settings select, .js-agent-action-settings textarea');
    const settingsDropdowns = row.querySelectorAll<HTMLElement>('.js-agent-action-settings .ui.dropdown');
    const eventDropdowns = row.querySelectorAll<HTMLElement>('.js-agent-action-events-dropdown');
    const eventInputs = row.querySelectorAll<HTMLInputElement>('.js-agent-action-events-cell input');

    const setEnabledState = () => {
      const isEnabled = enabledInput.checked;
      row.classList.toggle('is-disabled', !isEnabled);
      for (const input of settingsInputs) {
        if (input === enabledInput) continue;
        if (input.name === enabledInput.name) continue;
        input.disabled = !isEnabled;
      }
      for (const input of eventInputs) {
        input.disabled = !isEnabled;
      }
      for (const dropdown of settingsDropdowns) {
        dropdown.classList.toggle('disabled', !isEnabled);
      }
      for (const dropdown of eventDropdowns) {
        dropdown.classList.toggle('disabled', !isEnabled);
      }
    };

    enabledInput.addEventListener('change', setEnabledState);
    setEnabledState();
  }

  initAgentSecurityScheduleToggle();
  initAgentActionUserPickers();
}

// the reviewer and assignee settings of an action accept several agents and several human users at
// the same time, the human users are kept as one hidden input per selected account
function initAgentActionUserPickers(): void {
  const pickers = document.querySelectorAll<HTMLElement>('.js-agent-user-picker');
  for (const picker of pickers) {
    const inputName = picker.getAttribute('data-input-name');
    const searchBox = picker.querySelector<HTMLElement>('.ui.search');
    const addButton = picker.querySelector<HTMLButtonElement>('.js-agent-user-picker-add');
    const list = picker.querySelector<HTMLElement>('.js-agent-user-picker-list');
    if (!inputName || !searchBox || !addButton || !list) continue;
    const removeText = list.getAttribute('data-remove-text') || 'Remove';

    attachSearchBox(searchBox, `${appSubUrl}/user/search_candidates?q={query}&orgs=false&bots=false`, (response: AgentUserSearchResponse) => {
      return response.data.map((item) => ({title: item.login, image: item.avatar_url, description: item.full_name}));
    });

    addButton.addEventListener('click', () => {
      const prompt = searchBox.querySelector<HTMLInputElement>('input.prompt');
      if (!prompt) return;
      const value = prompt.value.trim();
      prompt.value = '';
      if (!value) return;
      if (list.querySelector(`input[name="${CSS.escape(inputName)}"][value="${CSS.escape(value)}"]`)) return;

      const item = document.createElement('div');
      item.className = 'item tw-items-center js-agent-user-picker-item';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.value = value;
      const label = document.createElement('div');
      label.className = 'item-main';
      label.textContent = value;
      const trailing = document.createElement('div');
      trailing.className = 'item-trailing';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ui mini red button js-agent-user-picker-remove';
      remove.textContent = removeText;
      trailing.append(remove);
      item.append(input, label, trailing);
      list.append(item);
    });

    list.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.js-agent-user-picker-remove');
      if (!target) return;
      target.closest('.item')!.remove();
    });
  }
}

// the recurring scan is switched on by the "scheduled scan" trigger event, its interval is
// pointless without it
function initAgentSecurityScheduleToggle(): void {
  const eventsInput = document.querySelector<HTMLInputElement>('input[name="action_events_security_scan"]');
  const settings = document.querySelector<HTMLElement>('.js-agent-security-schedule-settings');
  if (!eventsInput || !settings) return;

  const update = () => {
    const scheduled = eventsInput.value.split(',').some((event) => event.trim() === 'security_scan');
    settings.classList.toggle('tw-hidden', !scheduled);
  };
  eventsInput.addEventListener('change', update);
  update();
}

// the "match actions" logo has to follow the selected capability, so the create form always
// previews the avatar the new agent is going to get
export function initAgentAvatarPresets(): void {
  const container = document.querySelector<HTMLElement>('.js-agent-avatar-presets');
  const autoImage = container?.querySelector<HTMLImageElement>('.js-agent-avatar-auto-image');
  if (!container || !autoImage) return;

  type AgentAvatarRules = {rules: Array<[string, string]>; fallback: string};
  const rulesData = JSON.parse(container.getAttribute('data-avatar-rules') || '{"rules":[],"fallback":"general"}') as AgentAvatarRules;
  const images = new Map<string, string>();
  for (const input of container.querySelectorAll<HTMLInputElement>('input[data-preset]')) {
    const image = input.closest('label')?.querySelector<HTMLImageElement>('img');
    if (image) images.set(input.getAttribute('data-preset')!, image.getAttribute('src')!);
  }

  const capabilityInputs = document.querySelectorAll<HTMLInputElement>('.js-agent-capability');
  const update = () => {
    const capability = document.querySelector<HTMLInputElement>('.js-agent-capability:checked')?.value || '';
    const preset = rulesData.rules.find(([action]) => action === capability)?.[1] || rulesData.fallback;
    const image = images.get(preset);
    if (image) autoImage.setAttribute('src', image);
  };
  for (const input of capabilityInputs) {
    input.addEventListener('change', update);
  }
  update();
}

type AgentUserSearchResponse = {data: Array<{id: number; login: string; avatar_url: string; full_name: string; is_active: boolean}>};
type AgentTeamSearchResponse = {data: Array<{id: number; name: string; permission: string}>};
type AgentOrgSearchResponse = {data: Array<{id: number; name: string; full_name: string; avatar_url: string}>};
type AgentUserGroupSearchResponse = {data: Array<{id: number; slug: string; name: string}>};

export function initAgentPermissionCommandMode(): void {
  const modeSelect = document.querySelector<HTMLSelectElement>('select[name="command_mode"]');
  if (!modeSelect) return;
  const principals = document.querySelector<HTMLElement>('.js-agent-command-principals');
  if (!principals) return;

  const updateVisibility = () => {
    principals.classList.toggle('tw-hidden', modeSelect.value !== 'listed');
  };
  modeSelect.addEventListener('change', updateVisibility);
  updateVisibility();
}

// the access lists of an agent and of a global agent connection are built the same way: search a
// principal, add it, remove it again. Every entry posts the id of the principal in its own hidden
// input, the readable name is only shown, so a renamed account or an LDAP group whose slug is a
// distinguished name stays correct without the admin ever typing an identifier
export function initAgentPrincipalPickers(): void {
  const pickers = document.querySelectorAll<HTMLElement>('.js-agent-principal-picker');
  for (const picker of pickers) {
    const inputName = picker.getAttribute('data-input-name')!;
    const kind = picker.getAttribute('data-search-kind')!;
    const searchBox = picker.querySelector<HTMLElement>('.ui.search')!;
    const prompt = searchBox.querySelector<HTMLInputElement>('input.prompt')!;
    const addButton = picker.querySelector<HTMLButtonElement>('.js-agent-principal-picker-add')!;
    const list = picker.querySelector<HTMLElement>('.js-agent-principal-picker-list')!;
    const removeText = list.getAttribute('data-remove-text') || 'Remove';

    // only a result of the search can be added, nothing is created here, so the id of the picked
    // result is remembered until it is added or the keyword is edited again
    let selected: SearchResult | null = null;
    const onSelect = (result: SearchResult) => {
      selected = result;
    };

    if (kind === 'user_group') {
      const orgName = picker.getAttribute('data-org-name');
      const orgParam = orgName ? `&org=${encodeURIComponent(orgName)}` : '';
      attachSearchBox(searchBox, `${appSubUrl}/user/search_agent_user_group_candidates?q={query}${orgParam}`, (response: AgentUserGroupSearchResponse) => {
        return response.data.map((item) => ({title: item.name, value: String(item.id), description: item.name === item.slug ? '' : item.slug}));
      }, {onSelect});
    } else if (kind === 'org') {
      attachSearchBox(searchBox, `${appSubUrl}/user/search_agent_org_candidates?q={query}`, (response: AgentOrgSearchResponse) => {
        return response.data.map((item) => ({title: item.name, value: String(item.id), image: item.avatar_url, description: item.full_name}));
      }, {onSelect});
    } else if (kind === 'team') {
      const orgName = picker.getAttribute('data-org-name')!;
      attachSearchBox(searchBox, `${appSubUrl}/org/${orgName}/teams/-/search?q={query}`, (response: AgentTeamSearchResponse) => {
        return response.data.map((item) => ({title: item.name, value: String(item.id), description: item.permission}));
      }, {onSelect});
    } else {
      const inactiveText = picker.getAttribute('data-inactive-text') || '';
      // the EE endpoint also returns inactive users, they can be listed before their account is activated
      attachSearchBox(searchBox, `${appSubUrl}/user/search_agent_candidates?q={query}`, (response: AgentUserSearchResponse) => {
        return response.data.map((item) => ({
          title: item.login,
          value: String(item.id),
          image: item.avatar_url,
          description: item.is_active ? item.full_name : [item.full_name, inactiveText].filter(Boolean).join(' · '),
        }));
      }, {onSelect});
    }

    const addItem = () => {
      const picked = selected?.title === prompt.value.trim() ? selected : null;
      selected = null;
      if (!picked?.value) return; // a keyword that was not picked from the list has no id to post
      prompt.value = '';
      if (list.querySelector(`input[name="${CSS.escape(inputName)}"][value="${CSS.escape(picked.value)}"]`)) return;

      const item = document.createElement('div');
      item.className = 'item tw-items-center js-agent-principal-picker-item';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.value = picked.value;
      const main = document.createElement('div');
      main.className = 'item-main';
      const title = document.createElement('div');
      title.className = 'item-title';
      title.textContent = picked.title;
      main.append(title);
      if (picked.description) {
        const body = document.createElement('div');
        body.className = 'item-body';
        body.textContent = picked.description;
        main.append(body);
      }
      const trailing = document.createElement('div');
      trailing.className = 'item-trailing';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ui mini red button js-agent-principal-picker-remove';
      remove.textContent = removeText;
      trailing.append(remove);
      item.append(input, main, trailing);
      list.append(item);
    };

    addButton.addEventListener('click', addItem);
    prompt.addEventListener('keydown', (event) => {
      // the search box itself handles Enter while a result is highlighted, otherwise Enter adds the
      // picked result instead of submitting the whole form
      if (event.key !== 'Enter' || event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      addItem();
    });

    list.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.js-agent-principal-picker-remove');
      if (!target) return;
      target.closest('.item')!.remove();
    });
  }
}

// the user and organization access lists only matter for the "listed" access mode
export function initAgentConnectionAccessModes(): void {
  const toggles: Array<[string, string]> = [
    ['connection_user_access_mode', '.js-agent-connection-user-principals'],
    ['connection_org_access_mode', '.js-agent-connection-org-principals'],
  ];
  for (const [selectName, listSelector] of toggles) {
    const modeSelect = document.querySelector<HTMLSelectElement>(`select[name="${CSS.escape(selectName)}"]`);
    const principals = document.querySelector<HTMLElement>(listSelector);
    if (!modeSelect || !principals) continue;
    const update = () => principals.classList.toggle('tw-hidden', modeSelect.value !== 'listed');
    modeSelect.addEventListener('change', update);
    update();
  }
}

export function initAgentCodetServerModelReload(): void {
  const sourceSelects = document.querySelectorAll<HTMLSelectElement>('.js-codet-server-source-select');
  if (!sourceSelects.length) return;

  for (const sourceSelect of sourceSelects) {
    const form = sourceSelect.closest('form');
    if (!form) continue;
    const modelSelect = form.querySelector<HTMLSelectElement>('.js-agent-model-select');
    if (!modelSelect) continue;
    const modelsURL = sourceSelect.getAttribute('data-models-url');
    if (!modelsURL) continue;
    const languageSelect = form.querySelector<HTMLSelectElement>('.js-agent-language-select');

    sourceSelect.addEventListener('change', async () => {
      const selectedModel = modelSelect.value || modelSelect.getAttribute('data-selected-model') || '';
      const selectedLanguage = languageSelect?.value || languageSelect?.getAttribute('data-selected-language') || '';
      const params = new URLSearchParams({codet_server_id: sourceSelect.value});
      const response = await fetch(`${modelsURL}?${params.toString()}`); // eslint-disable-line no-restricted-globals
      const data = await response.json();
      if (!response.ok || !data.ok) return;

      modelSelect.replaceChildren();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = modelSelect.getAttribute('data-placeholder') || '';
      modelSelect.append(placeholder);

      for (const model of data.models ?? []) {
        const option = document.createElement('option');
        option.value = model.id;
        option.textContent = model.title;
        if (model.id === selectedModel) option.selected = true;
        modelSelect.append(option);
      }

      // the working languages are reported by the same codet server, refresh them together
      if (!languageSelect) return;
      const autoText = languageSelect.getAttribute('data-auto-text')!;
      languageSelect.replaceChildren();
      for (const language of data.languages ?? []) {
        const option = document.createElement('option');
        option.value = language.value;
        option.textContent = language.value === 'auto' ? autoText : language.title;
        if (language.value === selectedLanguage) option.selected = true;
        languageSelect.append(option);
      }
    });
  }
}
